import { Component, Element, Event, EventEmitter, Fragment, h, Method, Prop, State } from '@stencil/core';
import { VALIDATION_ATTRIBUTES } from '../../vs-form-builder.model';
import { checkValidations, getValidStateAccordingToRequiredStatus } from '../../helpers/validator.helper';
import { CardInputAttr } from './vs-card-input.model';
import { decodeHTMLEntities } from '@src/components/vs-form-builder/helpers/rules.helper';

@Component({
  tag: 'vs-card-input',
  styleUrl: 'vs-card-input.css',
  shadow: false,
})

export class VsCardInput {

  /**
   * @shortDescription Identifier of the input field.
   * @controlName inputId */
  @Prop() inputId: string;

  /**
   * @shortDescription Index of input field in a form.
   * @controlName index */
  @Prop() index: number;

  /**
   * @shortDescription Define the label for input field.
   * @controlName label */
  @Prop() label: string;

  /**
   * @shortDescription Label exists in the input field as eyebrow view.
   * @controlName eyebrow */
  @Prop() eyebrow: boolean = false;

  /**
   * @shortDescription If declared, it is the input's default value.
   * @controlName defaultValue */
  @Prop() defaultValue: string = '';

  /**
   * @shortDescription Input's type
   * @controlName type */
  @Prop() type: string = 'text';

  /**
   * @shortDescription Validators rules to check against for the input.
   * @controlName validators */
  @Prop() validators: any[] = [];

  /**
   * @shortDescription Tooltip text for the input field.
   * @controlName tooltip */
  @Prop() tooltip: string;

  /**
   @shortDescription required text label
   @controlName requiredText
   */
  @Prop() requiredText: string;

  /* START GENAI@GHCOPILOT */
  /**
   * @shortDescription Flag to show first four card number
   * @controlName isShowFirstFourCardNumber
   */
  @Prop() isShowFirstFourCardNumber: boolean;
  /* END GENAI@GHCOPILOT */

  /* START GENAI@GHCOPILOT */
  /**
   * @shortDescription HTML content to be inserted above the form field
   * @controlName aboveInputContent
   */
  @Prop() aboveInputContent: string;

  /**
   * @shortDescription HTML content to be inserted below the form field
   * @controlName belowInputContent
   */
  @Prop() belowInputContent: string;

  /**
   * @shortDescription HTML content to be inserted after the form field
   * @controlName betweenInputContent
   */
  @Prop() betweenInputContent: string;
  /* END GENAI@GHCOPILOT */

  @State() showError: boolean = false;
  @State() value: string = '';
  @State() selectedErrorMessage: string = '';
  @State() valid: boolean = true;
  @State() inputAllyAlert: string = 'input_alert_';
  @State() isRequired: boolean;

  /**
   * @shortDescription Emit Input blur event to parent component on input blur
   * @controlName blurEvent */
  @Event() blurEvent: EventEmitter<void>;

  private inputEl?;
  @Element() el: HTMLElement;

  /**
   * @shortDescription Emit input value on change
   * @controlName computedInputValue */
  @Event({
    eventName: 'computedInputValue',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) computedInputValue: EventEmitter;

  /**
   * @shortDescription Reset value when reset button is clicked
   * @controlName resetValue */
  @Method()
  async resetValue(isShowError = false) {
    const cardName = `${this.inputId}cardPart_`;
    let inputs = this.el.querySelectorAll(`[id^=\"${cardName}\"]`);
    for (let i = 0; i < inputs.length; i++) {
      (document.getElementById(inputs[i].id) as HTMLInputElement).value = this.defaultValue;
    }
    this.showError = isShowError;
  }

  /**
   * @shortDescription set input focus
   * @controlName setFocus */
  @Method()
  async setFocus() {
    this.showError = true;
    this.inputEl.focus();
  }


  componentWillLoad() {
    this.isRequired = this.validators.findIndex(validator => validator.key === VALIDATION_ATTRIBUTES.REQUIRE) > -1;
    this.updateInput(this.defaultValue);
  }

  private onBlurInput = () => {
    this.showError = true;
    this.blurEvent.emit();
  };

  private validate = (value: string) => {
    const validationAttr = checkValidations(this.validators, value);
    this.selectedErrorMessage = validationAttr.errorMessage;
    this.valid = getValidStateAccordingToRequiredStatus(this.isRequired, value, validationAttr);
  };

  private onInputChange = (event) => {
    const { target } = event;
    this.updateInput(target.value.replace(/,/g, ''));
  };


  private updateInput = (value) => {
    this.value = value;
    this.validate(this.getFullInput());
    this.computedInputValue.emit(
      {
        index: this.index,
        key: this.inputId,
        value: this.getFullInput(),
        isValid: this.valid,
        error: this.selectedErrorMessage,
      } as CardInputAttr,
    );
  };

  private getFullInput = () => {
    const cardName = `${this.inputId}cardPart_`;
    let inputs = this.el.querySelectorAll(`[id^=\"${cardName}\"]`);
    let value = '';
    for (let i = 0; i < inputs.length; i++) {
      value = value + (document.getElementById(inputs[i].id) as HTMLInputElement).value;
    }
    return value.toString().trim();
  };

  watchLength(step: number, event: KeyboardEvent) {
    const currentStepInputElement = this.el.querySelector(`#${this.inputId}cardPart_${step}`) as HTMLInputElement;
    const nonInputKeys = ['Backspace', 'Tab', 'Shift', 'Delete'];

    if (currentStepInputElement.value.length === 4 && step < 4 && !nonInputKeys.includes(event.key)) {
      (this.el.querySelector(`#${this.inputId}cardPart_${step + 1}`) as HTMLInputElement).focus();
    }
    if (currentStepInputElement.value.length === 0 && step > 1 && (event.key === 'Backspace' || event.key === 'Delete')) {
      (this.el.querySelector(`#${this.inputId}cardPart_${step - 1}`) as HTMLInputElement).focus();
    }
  }


  private generateAriaLabel = () => {
    return (this.showError && !this.valid && this.selectedErrorMessage) ? this.inputAllyAlert + this.inputId : undefined;
  };

  render() {
    return (
      <Fragment>
        {this.aboveInputContent && <div innerHTML={this.aboveInputContent} />}
        <div class='vs-form-group vs-row vs-mb-1 vs-mx-auto'>
          <fieldset class='vs-form-group vs-card-fieldset vs-px-0 vs-mb-0 vs-pb-0'>
            {this.label && (
              <Fragment>
                <div class='vs-legend-container'>
                  <legend class='vs-card-legend vs-row vs-mx-0 vs-text vs-mb-0'>
                    <span class='vs-text vs-mb-1' innerHTML={this.label} />
                    {this.isRequired ? <strong class='required'><i> {this.requiredText} </i></strong> : ''}
                  </legend>
                  {this.tooltip?.length > 0 && <vs-tooltip
                    tooltip-id={`tooltip${this.inputId}`}
                    ally-open-label={`Open tooltip for ${this.label}`}
                    tooltip-header={this.label}
                    tooltip-body={this.tooltip} />
                  }
                </div>
              </Fragment>
            )}
            {this.betweenInputContent &&  <div class="vs-text vs-mb-0" innerHTML={this.betweenInputContent} />}
            <div class='vs-row vs-mr-auto vs-px-0 vs-card-field vs-mb-0'>
              <div class='vs-d-inline-block vs-col-3 vs-pr-0'>
                <input ref={el => this.inputEl = el as HTMLInputElement}
                       class={`form-control vs-input vs-pr-0 ${this.showError && !this.valid ? 'vs-error ng-dirty ng-invalid' : ''}`}
                       id={`${this.inputId}cardPart_1`}
                       name={`${this.inputId}cardPart_1`}
                       type={this.isShowFirstFourCardNumber ? 'text' : 'password'}
                       style={{"-webkit-text-security": `${this.isShowFirstFourCardNumber ? undefined:'disc'}`}}
                       data-vv-validate-on='blur'
                       onKeyDown={(event: KeyboardEvent) => this.watchLength(1, event)}
                       onInput={this.onInputChange}
                       aria-invalid={this.showError && !this.valid}
                       maxLength={4}
                       aria-label='1st Set Of Four Digits'
                       aria-describedby={this.generateAriaLabel()}
                />
              </div>
              <div class='vs-d-inline-block vs-col-3 vs-pr-0'>
                <input id={`${this.inputId}cardPart_2`}
                       name={`${this.inputId}cardPart_2`}
                       maxLength={4} type='text'
                       style={{"-webkit-text-security": "disc"}}
                       data-vv-validate-on='blur'
                       class={`form-control vs-input vs-pr-0 ${this.showError && !this.valid ? 'vs-error ng-dirty ng-invalid ' : ''}`}
                       onKeyDown={(event: KeyboardEvent) => this.watchLength(2, event)}
                       onInput={this.onInputChange}
                       aria-invalid={this.showError && !this.valid}
                       aria-label='2nd Set Of Four Digits'
                       aria-describedby={this.generateAriaLabel()}
                />
              </div>
              <div class='vs-d-inline-block vs-col-3 vs-pr-0'>
                <input id={`${this.inputId}cardPart_3`}
                       name={`${this.inputId}cardPart_3`}
                       maxLength={4} type='text'
                       style={{"-webkit-text-security": "disc"}}
                       data-vv-validate-on='blur'
                       class={`form-control vs-input vs-pr-0 ${this.showError && !this.valid ? 'vs-error ng-dirty ng-invalid ' : ''}`}
                       onKeyDown={(event: KeyboardEvent) => this.watchLength(3, event)}
                       onInput={this.onInputChange}
                       aria-invalid={this.showError && !this.valid}
                       aria-label='3rd Set Of Four Digits'
                       aria-describedby={this.generateAriaLabel()}
                />
              </div>
              <div class='vs-d-inline-block vs-col-3 vs-pr-0'>
                <input
                  id={`${this.inputId}cardPart_4`}
                  name={`${this.inputId}cardPart_4`}
                  maxLength={4} type='text'
                  data-vv-validate-on='blur'
                  class={`form-control vs-input vs-pr-0 ${this.showError && !this.valid ? 'vs-error ng-dirty ng-invalid ' : ''}`}
                  onKeyDown={(event: KeyboardEvent) => this.watchLength(4, event)}
                  onInput={this.onInputChange}
                  onBlur={this.onBlurInput}
                  aria-invalid={this.showError && !this.valid}
                  aria-label='4th Set Of Four Digits'
                  aria-describedby={this.generateAriaLabel()}
                />
              </div>
            </div>
            {this.showError && !this.valid && this.selectedErrorMessage && (
              <p role='alert' aria-live='polite' class='vs-meta vs-text vs-alert vs-inline-alert vs-pb-1'
                 id={this.inputAllyAlert + this.inputId}>
                <span aria-hidden='true' class='visa-icon-alert' />
                <span innerHTML={decodeHTMLEntities(this.selectedErrorMessage)} />
              </p>
            )}
          </fieldset>
        </div>
        {this.belowInputContent &&  <div class="vs-text vs-mb-0" innerHTML={this.belowInputContent} />}
      </Fragment>
    );
  };
}
